import { BlueButton } from '../../../../../customComponents/buttons/BlueButton'
import { IosShare } from '@mui/icons-material'
import generatePDF, { Margin } from 'react-to-pdf'
import { RCNEndOfYearSummary } from './RCNEndOfYearSummary'
import { ISDEndOfYearSummary } from './ISDEndOfYearSummary'
import { Modal } from '../../../../../customComponents/Modal'
import { type SurveySchema, SurveyType } from '../../../../../../models/SurveySchema'
import { type SurveyContent } from '../../MidYear/Summary/MidYearSummary'
import { Box, Card, CircularProgress, Stack, Typography, type SxProps, type Theme } from '@mui/material'
import { useState } from 'react'

const PDF_ELEMENT_ID = 'eoy-summary-pdf'

const delay = async (ms: number): Promise<void> => {
  await new Promise((resolve) => setTimeout(resolve, ms))
}

const styles: Record<string, SxProps<Theme>> = {
  container: {
    backgroundColor: 'var(--BaseTones-primitive-nuetral-50, rgba(238, 238, 239, 1))',
    borderRadius: '12px',
    padding: '20px 40px 40px 40px',
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    position: 'relative',
    overflow: 'visible'
  },
  section: {
    backgroundColor: 'var(--BaseTones-primitive-surface-100, rgba(255, 255, 255, 1))',
    padding: '32px 40px 40px 40px',
    borderRadius: '8px',
    border: '1px solid var(--Theme-primitive-primary-300, rgba(84, 153, 195, 1))',
    boxShadow: '0px 2px 7px -1px var(--BaseTones-primitive-nuetral-900-a8, rgba(37, 36, 38, 0.08)), 0px 1px 0px 0px var(--BaseTones-primitive-nuetral-900-a8, rgba(37, 36, 38, 0.08))'
  }
}

export const EndOfYearSummary = ({ initialSurvey }: { initialSurvey: SurveySchema }): JSX.Element => {
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const content: SurveyContent = JSON.parse(initialSurvey.content)
  const answers: Record<string, any> = initialSurvey.answers != null ? JSON.parse(initialSurvey.answers) : {}

  const exportPDF = async (): Promise<void> => {
    const target = document.getElementById(PDF_ELEMENT_ID)
    if (target == null) return

    const originalWidth = target.style.width
    try {
      setIsGeneratingPdf(true)
      target.style.width = '1000px'
      await delay(1000)
      await generatePDF(() => target, {
        method: 'open',
        resolution: 3,
        page: { margin: Margin.MEDIUM, format: 'letter', orientation: 'landscape' },
        overrides: { canvas: { ignoreElements: (el) => el.hasAttribute('data-hide-in-pdf') } }
      })
    } finally {
      target.style.width = originalWidth
      setIsGeneratingPdf(false)
    }
  }

  if (content == null) return <div>No End of Year Summary found</div>

  return (<>
    <Card sx={styles.container} data-testid='endOfYearSummaryCard'>
      <Stack gap='20px'>
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Typography variant='h1'>End of Year Progress Report Submission</Typography>
          <BlueButton
            onClick={exportPDF}
            endIcon={<IosShare />}
            disabled={isGeneratingPdf}
          >
            Export
          </BlueButton>
        </Stack>

        <div id={PDF_ELEMENT_ID}>
          <Stack sx={styles.section} gap={2}>
            {initialSurvey.id === SurveyType.RCNEndOfYear
              ? <RCNEndOfYearSummary content={content} answers={answers} />
              : <ISDEndOfYearSummary content={content} answers={answers} />}
          </Stack>
        </div>
      </Stack>
    </Card>

    {isGeneratingPdf && (
      <Modal
        data-hide-in-pdf
        open={true}
        title='Generating PDF'
        onConfirm={() => {}}
        onClose={() => {}}
        hideCancelButton={true}
        hideXtoClose={true}
        hideConfirmButton={true}
        bodyContent={
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CircularProgress />
            <Typography variant='body1' mt={2}>
              Generating PDF, please wait...
            </Typography>
          </Box>
        }
      />
    )}
  </>)
}
